import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Package, Search, ChevronLeft, ChevronRight, RefreshCw, Save } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const SupplierInventory = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [stockUpdates, setStockUpdates] = useState({});
  const [saving, setSaving] = useState({});

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const supplierId = user?._id;
      
      if (!supplierId) {
        setLoading(false);
        return;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/get-all-products?supplier=${supplierId}`
      );
      setProducts(res.data.products || []);
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleStockChange = (productId, newStock) => {
    setStockUpdates(prev => ({ ...prev, [productId]: newStock }));
  };

  const handleSaveStock = async (productId) => {
    if (stockUpdates[productId] === undefined) return;
    
    setSaving(prev => ({ ...prev, [productId]: true }));
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/products/update-product/${productId}`,
        { stock: parseInt(stockUpdates[productId]) }
      );
      // Remove from updates once saved successfully
      setStockUpdates(prev => {
        const newState = { ...prev };
        delete newState[productId];
        return newState;
      });
      // Refresh to get latest data
      fetchProducts();
    } catch (err) {
      console.error("Error saving stock:", err);
      alert("Failed to update stock");
    } finally {
      setSaving(prev => ({ ...prev, [productId]: false }));
    }
  };

  const filteredProducts = products.filter(
    (product) =>
      product.name?.en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredProducts.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const currentProducts = filteredProducts.slice(startIndex, startIndex + entriesPerPage);

  const SkeletonRow = () => (
    <tr className="border-b border-gray-100">
      {[...Array(5)].map((_, i) => (
        <td key={i} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
    </tr>
  );

  return (
    <section className="min-h-screen p-6 bg-[#EEF3F7]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl md:text-3xl font-philosopher text-primaryTextColor flex items-center gap-2">
          <Package className="text-[#fba93d] w-7 h-7" />
          Inventory Management
        </h1>
        <button
          onClick={fetchProducts}
          className="flex items-center gap-2 bg-[#ea8d2a] text-white px-4 py-2 rounded-lg hover:bg-orange-600"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search products by name or SKU..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full md:w-96 focus:outline-none focus:ring-1 focus:ring-[#ea8d2a]"
          />
          <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Image</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Product</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">SKU</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Current Stock</th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => <SkeletonRow key={i} />)
              ) : currentProducts.length > 0 ? (
                currentProducts.map((product) => (
                  <tr key={product._id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                    <td className="px-4 py-4">
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt={product.name?.en} className="w-12 h-12 rounded object-cover border" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-gray-400">No Img</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{product.name?.en || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{product.sku}</td>
                    <td className="px-4 py-4">
                      <input 
                        type="number"
                        min="0"
                        className="border border-gray-300 rounded px-2 py-1 w-24 text-sm focus:outline-none focus:ring-1 focus:ring-[#ea8d2a]"
                        value={stockUpdates[product._id] !== undefined ? stockUpdates[product._id] : (product.stock || 0)}
                        onChange={(e) => handleStockChange(product._id, e.target.value)}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleSaveStock(product._id)}
                        disabled={stockUpdates[product._id] === undefined || saving[product._id]}
                        className={`flex items-center gap-1 px-3 py-1 rounded text-sm text-white ${
                          stockUpdates[product._id] !== undefined && !saving[product._id]
                            ? 'bg-green-600 hover:bg-green-700' 
                            : 'bg-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Save className="w-4 h-4" /> {saving[product._id] ? 'Saving' : 'Save'}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No products found in inventory</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {!loading && filteredProducts.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(startIndex + entriesPerPage, filteredProducts.length)} of {filteredProducts.length} items
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SupplierInventory;
